import { useQueryClient } from '@tanstack/react-query'
import { api } from '../http'
import { buildJsonApiParams } from '../jsonApi'
import { queryKeys } from '../queryKeys'
import { useApiQuery, useApiMutation } from './_base'

// ─────────────────────────────────────────────────────────────────────────────
// Core: per-company social networks list + CRUD
// ─────────────────────────────────────────────────────────────────────────────

/** GET /companies/{companyId}/socialNetworks */
export function useCompanySocialNetworks(params = {}, options = {}) {
    const { companyId, ...rest } = params
    const apiParams = buildJsonApiParams(rest)
    return useApiQuery({
        queryKey: queryKeys.socialNetworks.list({ companyId, ...apiParams }),
        url: `/companies/${companyId}/socialNetworks`,
        params: apiParams,
        options: { enabled: !!companyId, staleTime: 60_000, ...options },
    })
}

/** GET /socialNetworks/{id} */
export function useSocialNetwork(id, params = {}, options = {}) {
    const apiParams = buildJsonApiParams(params)
    return useApiQuery({
        queryKey: queryKeys.socialNetworks.detail(id, apiParams),
        url: `/socialNetworks/${id}`,
        params: apiParams,
        options: { enabled: !!id, ...options },
    })
}

/** POST /companies/{companyId}/socialNetworks (connect new account) */
export function useCreateSocialNetwork() {
    const qc = useQueryClient()
    return useApiMutation({
        mutationKey: ['socialNetworks', 'create'],
        mutationFn: ({ companyId, ...payload }) =>
            api.post(`/companies/${companyId}/socialNetworks`, {}, payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.socialNetworks.all })
            qc.invalidateQueries({ queryKey: queryKeys.companies.all })
        },
    })
}

/** PUT /socialNetworks/{id} */
export function useUpdateSocialNetwork() {
    const qc = useQueryClient()
    return useApiMutation({
        mutationKey: ['socialNetworks', 'update'],
        mutationFn: ({ id, ...payload }) =>
            api.put(`/socialNetworks/${id}`, {}, payload),
        onSuccess: (_d, { id }) => {
            qc.invalidateQueries({
                queryKey: queryKeys.socialNetworks.detail(id),
            })
            qc.invalidateQueries({
                queryKey: queryKeys.socialNetworks.lists(),
            })
        },
    })
}

/** DELETE /socialNetworks/{id} */
export function useDeleteSocialNetwork() {
    const qc = useQueryClient()
    return useApiMutation({
        mutationKey: ['socialNetworks', 'delete'],
        mutationFn: (id) => api.delete(`/socialNetworks/${id}`),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.socialNetworks.all })
            qc.invalidateQueries({ queryKey: queryKeys.posts.all })
            qc.invalidateQueries({ queryKey: queryKeys.postGroups.all })
        },
    })
}

/** POST /socialNetworks/{id}/proxy — proxy a raw call through the backend. */
export function useSocialNetworkProxy() {
    return useApiMutation({
        mutationKey: ['socialNetworks', 'proxy'],
        mutationFn: ({ id, ...payload }) =>
            api.post(`/socialNetworks/${id}/proxy`, {}, payload),
    })
}

/** GET /socialNetworks/{id}/pages */
export function useSocialNetworkPages(socialNetworkId, options = {}) {
    return useApiQuery({
        queryKey: queryKeys.socialNetworks.custom('pages', {
            id: String(socialNetworkId),
        }),
        url: `/socialNetworks/${socialNetworkId}/pages`,
        options: { enabled: !!socialNetworkId, staleTime: 60_000, ...options },
    })
}

// ─────────────────────────────────────────────────────────────────────────────
// Pinterest
// ─────────────────────────────────────────────────────────────────────────────

/** GET /socialNetworks/{id}/boards */
export function usePinterestBoards(
    socialNetworkId,
    { page_size } = {},
    options = {}
) {
    return useApiQuery({
        queryKey: queryKeys.socialNetworks.custom('pinterestBoards', {
            id: String(socialNetworkId),
            page_size,
        }),
        url: `/socialNetworks/${socialNetworkId}/boards`,
        params: page_size ? { page_size } : undefined,
        options: { enabled: !!socialNetworkId, ...options },
    })
}

/** POST /socialNetworks/{id}/boards */
export function useCreatePinterestBoard() {
    const qc = useQueryClient()
    return useApiMutation({
        mutationKey: ['socialNetworks', 'createPinterestBoard'],
        mutationFn: ({ socialNetworkId, ...payload }) =>
            api.post(
                `/socialNetworks/${socialNetworkId}/boards`,
                {},
                payload
            ),
        onSuccess: (_d, { socialNetworkId }) => {
            qc.invalidateQueries({
                queryKey: queryKeys.socialNetworks.custom('pinterestBoards', {
                    id: String(socialNetworkId),
                }),
            })
        },
    })
}

/** GET /posts/{postId}/pinAnalytics */
export function usePinAnalytics(postId, options = {}) {
    return useApiQuery({
        queryKey: queryKeys.posts.custom('pinAnalytics', String(postId)),
        url: `/posts/${postId}/pinAnalytics`,
        options: { enabled: !!postId, ...options },
    })
}

// ─────────────────────────────────────────────────────────────────────────────
// YouTube
// ─────────────────────────────────────────────────────────────────────────────

/** GET /socialNetworks/{id}/categories */
export function useYoutubeCategories(socialNetworkId, params = {}, options = {}) {
    return useApiQuery({
        queryKey: queryKeys.socialNetworks.custom('youtubeCategories', {
            id: String(socialNetworkId),
            ...params,
        }),
        url: `/socialNetworks/${socialNetworkId}/categories`,
        params,
        options: { enabled: !!socialNetworkId, staleTime: 60_000, ...options },
    })
}

/** GET /socialNetworks/{id}/playlists */
export function useYoutubePlaylists(socialNetworkId, options = {}) {
    return useApiQuery({
        queryKey: queryKeys.socialNetworks.custom('youtubePlaylists', {
            id: String(socialNetworkId),
        }),
        url: `/socialNetworks/${socialNetworkId}/playlists`,
        options: { enabled: !!socialNetworkId, ...options },
    })
}

/** POST /socialNetworks/{id}/playlists */
export function useCreateYoutubePlaylist() {
    const qc = useQueryClient()
    return useApiMutation({
        mutationKey: ['socialNetworks', 'createYoutubePlaylist'],
        mutationFn: ({ socialNetworkId, ...payload }) =>
            api.post(
                `/socialNetworks/${socialNetworkId}/playlists`,
                {},
                payload
            ),
        onSuccess: (_d, { socialNetworkId }) => {
            qc.invalidateQueries({
                queryKey: queryKeys.socialNetworks.custom('youtubePlaylists', {
                    id: String(socialNetworkId),
                }),
            })
        },
    })
}

// ─────────────────────────────────────────────────────────────────────────────
// Canva
// ─────────────────────────────────────────────────────────────────────────────

/** GET /companies/{companyId}/canva/designs */
export function useCanvaDesigns(
    { companyId, ...params } = {},
    options = {}
) {
    return useApiQuery({
        queryKey: queryKeys.socialNetworks.custom('canvaDesigns', {
            companyId,
            ...params,
        }),
        url: `/companies/${companyId}/canva/designs`,
        params,
        options: { enabled: !!companyId, ...options },
    })
}

/**
 * GET /companies/{companyId}/canva/designExportJob/{id} — polled until status
 * indicates the export is finished.
 */
export function useCanvaExportJob({ companyId, id, intervalMs = 3000 } = {}, options = {}) {
    return useApiQuery({
        queryKey: queryKeys.socialNetworks.custom('canvaExportJob', {
            companyId,
            id: String(id),
        }),
        url: `/companies/${companyId}/canva/designExportJob/${id}`,
        options: {
            enabled: !!companyId && !!id,
            refetchInterval: (q) => {
                const status = q.state.data?.data?.status
                if (status === 'success' || status === 'failed') return false
                return intervalMs
            },
            staleTime: 0,
            ...options,
        },
    })
}

/** POST /companies/{companyId}/canva/designExportJob */
export function useCreateCanvaExportJob() {
    return useApiMutation({
        mutationKey: ['socialNetworks', 'createCanvaExportJob'],
        mutationFn: ({ companyId, ...payload }) =>
            api.post(
                `/companies/${companyId}/canva/designExportJob`,
                {},
                payload
            ),
    })
}
